import { createRawSnippet, mount } from "svelte";
import { createInertiaApp } from "@evento/svelte-inertia";
import { transformJSONString, transformPropsDeep } from "./index.js";
export function resolvePageData(id) {
    const element = document?.getElementById(id);
    if (!element) {
        return {};
    }
    return transformJSONString(element?.dataset?.page ?? "{}");
}
export function createDefaultFallback() {
    return createRawSnippet(() => ({
        render: () => "<div>Chyba!</div>",
    }));
}
export class Application {
    _id = "app";
    _dataResolver = resolvePageData;
    _debug = false;
    _progress = true;
    _color = "#560aec";
    _clearPageData = true;
    _fallback = createDefaultFallback();
    _pages = [];
    _pageResolver = async (name, pages, resolve) => await resolve(pages?.default, name);
    _layouts = [];
    _layoutResolver = async (_, layouts, resolve) => await resolve(layouts?.default);
    _resolver = this.resolver.bind(this);
    _transformDataInRouter = this.getDataResolver() !== null;
    _customApplicationComponent = null;
    setCustomApplicationComponent(customApplicationComponent) {
        this._customApplicationComponent = customApplicationComponent;
        return this;
    }
    getCustomApplicationComponent() {
        return this._customApplicationComponent;
    }
    setTransformDataInRouter(value) {
        this._transformDataInRouter = value;
        return this;
    }
    getTransformDataInRouter() {
        return this._transformDataInRouter;
    }
    getId() {
        return this._id;
    }
    setId(value) {
        this._id = value;
        return this;
    }
    getFallback() {
        return this._fallback;
    }
    setFallback(value) {
        this._fallback = value;
    }
    getData() {
        return this.getDataResolver()?.(this.getId());
    }
    getDataResolver() {
        return this._dataResolver;
    }
    setDataResolver(value) {
        this._dataResolver = value;
        return this;
    }
    getDebug() {
        return this._debug;
    }
    setDebug(value) {
        this._debug = value;
        return this;
    }
    getProgress() {
        if (this._progress === true) {
            return {
                color: this.getColor(),
            };
        }
        return this._progress;
    }
    setProgress(value) {
        this._progress = value;
        return this;
    }
    getPageResolver() {
        return this._pageResolver;
    }
    getLayoutResolver() {
        return this._layoutResolver;
    }
    setLayouts(value) {
        this._layouts = value;
        return this;
    }
    getLayouts() {
        return this._layouts;
    }
    setPageResolver(resolver) {
        this._pageResolver = resolver;
        return this;
    }
    setLayoutResolver(resolver) {
        this._layoutResolver = resolver;
        return this;
    }
    getClearPageData() {
        return this._clearPageData;
    }
    setCleaPageData(value) {
        this._clearPageData = value;
        return this;
    }
    setColor(value) {
        this._color = value;
        return this;
    }
    setResolver(value) {
        this._resolver = value;
        return this;
    }
    setPages(value) {
        this._pages = value;
        return this;
    }
    getColor() {
        return this._color;
    }
    getResolver() {
        return this._resolver;
    }
    getPages() {
        return this._pages;
    }
    build() {
        // @ts-expect-error
        let setup = ({ el, App, props }) => {
            mount(this.getCustomApplicationComponent() ?? App, {
                target: el,
                props: {
                    ...props,
                    transformDataInRouter: this.getTransformDataInRouter(),
                    dataInRouterTransformer: transformPropsDeep,
                },
            });
            if (this.getDebug()) {
                console.log(props);
            }
            if (this.getClearPageData()) {
                delete el.dataset.page;
            }
        };
        return {
            id: this.getId(),
            resolve: this.getResolver(),
            setup: setup.bind(this),
            progress: this.getProgress(),
            page: this.getData(),
        };
    }
    async run() {
        const data = this.build();
        return createInertiaApp(data);
    }
    itemResolver = async (item, name) => {
        if (name && item instanceof Object && name in item) {
            item = item[name];
        }
        // TODO: lepší kontrola jestli je třída nebo ne
        if (item instanceof Function) {
            try {
                item = item();
            }
            catch (e) {
                //
            }
        }
        if (item instanceof Promise) {
            item = await item;
        }
        return item;
    };
    async resolver(name) {
        const pages = this.getPages();
        const layouts = this.getLayouts();
        let page = await this.getPageResolver()(name, pages, this.itemResolver);
        let layout = await this.getLayoutResolver()(name, layouts, this.itemResolver);
        if (layout === undefined) {
            layout = page?.layout;
        }
        if (page?.default) {
            page = page.default;
        }
        if (!page) {
            console.warn(`Nelze najít stránku "${name}"!`);
            page = this.getFallback();
        }
        return {
            default: page,
            layout,
        };
    }
}
